import { useState } from "react";
import {
  Box,
  Typography,
  Popover,
  MenuItem,
  Divider,
  styled,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useNavigate } from "react-router-dom";
import { SidebarUserMenuSkeleton } from "./sidebar-user-menu-skeleton";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { clearAccessToken } from "@/redux/actions/auth-actions";
import { useDispatch } from "react-redux";
import { logoutUser } from "@/services/auth.service";
import { formatAccessType } from "@/utils/format-access-type";
import { getInitials } from "@/utils/get-initals";
import { useAdminUser } from "@/hooks/use-admin-user";
import { clearActiveSession, clearWayaOfficialUnlocked } from "@/utils/auth-session";

const UserFooter = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  cursor: "pointer",
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const UserAvatar = styled(Box)(({ theme }) => ({
  width: 34,
  height: 34,
  borderRadius: "50%",
  backgroundColor: theme.palette.primary.main,
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.8rem",
  fontWeight: 600,
  flexShrink: 0,
}));

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== "danger",
})(({ theme, danger }) => ({
  fontSize: "0.875rem",
  padding: theme.spacing(1, 2),
  minWidth: 160,
  color: danger ? theme.palette.error.main : "inherit",
  "&:hover": {
    backgroundColor: danger
      ? `${theme.palette.error.main}10`
      : theme.palette.action.hover,
  },
}));

const SidebarUserMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { data: adminUser, isLoading } = useAdminUser();

  const firstName = adminUser?.firstName ?? "";
  const lastName = adminUser?.surname ?? "";
  const email = adminUser?.email ?? "";
  const role = adminUser?.customerType; //to change to roles
  const initials = getInitials(firstName, lastName);

  const logoutMutation = useMutation({
    mutationFn: logoutUser,
    onSuccess: async () => {
      await queryClient.cancelQueries();
      queryClient.clear();
      dispatch(clearAccessToken());
      clearActiveSession();
      clearWayaOfficialUnlocked();
      navigate("/session/login", { replace: true });
    },
    onError: () => {
      queryClient.clear();
      dispatch(clearAccessToken());
      clearActiveSession();
      clearWayaOfficialUnlocked();
      navigate("/session/login", { replace: true });
    },
  });

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleProfile = () => {
    handleClose();
    navigate("/profile");
  };

  if (isLoading) {
    return <SidebarUserMenuSkeleton />;
  }

  return (
    <>
      <UserFooter onClick={handleOpen}>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
        >
          <UserAvatar>{initials}</UserAvatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {formatAccessType(firstName)} {formatAccessType(lastName)}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {formatAccessType(role)}
            </Typography>
          </Box>
        </Box>
        <KeyboardArrowUpIcon
          fontSize="small"
          sx={{
            color: "text.secondary",
            transform: isOpen ? "rotate(0deg)" : "rotate(180deg)",
            transition: "transform 200ms ease",
          }}
        />
      </UserFooter>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            elevation: 2,
            sx: { borderRadius: "8px", mb: 0.5, minWidth: 180 },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {formatAccessType(firstName)} {formatAccessType(lastName)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {email}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ py: 0.5 }}>
          <StyledMenuItem onClick={handleProfile}>Profile</StyledMenuItem>

          <Divider sx={{ my: 0.5 }} />

          <StyledMenuItem
            danger={1}
            disabled={logoutMutation.isPending}
            onClick={() => {
              handleClose();
              logoutMutation.mutate();
            }}
          >
            {logoutMutation.isPending ? "Logging out..." : "Logout"}
          </StyledMenuItem>
        </Box>
      </Popover>
    </>
  );
};

export default SidebarUserMenu;
